import { produce } from 'immer'
import { useEffect } from 'react'

import { getLocationPin, updateLocationPin } from '@/map'
import { isDeepEqual } from '@/server-data'
import type { LocationEditorFieldConfig } from '../types'

export function useConnectFormFieldToLocationPin(options: LocationEditorFieldConfig) {
  const { pinId, disabled, pinRadius, pinHidden, pinInfo, connectedPlaceId, pinType } = options

  // Hidden state, controlled by the form
  useEffect(() => {
    if (disabled) return
    const pin = getLocationPin(pinId, true)

    if (!pin) return
    if (typeof pinHidden !== 'boolean') return

    updateLocationPin(pinId, { hidden: pinHidden })
  }, [pinId, disabled, pinHidden])

  // Form Radius changed
  useEffect(() => {
    if (disabled) return
    const pin = getLocationPin(pinId, true)

    if (!pin) return
    if (typeof pinRadius !== 'number') return

    if (pin.pin.radius !== pinRadius) {
      updateLocationPin(pinId, prevPin =>
        produce(prevPin, draft => {
          draft.pin.radius = pinRadius
        }),
      )
    }
  }, [pinId, disabled, pinRadius])

  // Pin Info changed
  useEffect(() => {
    if (disabled) return
    const pin = getLocationPin(pinId, true)

    if (!pin) return
    if (typeof pinInfo !== 'object') return
    if (isDeepEqual(pin.info, pinInfo)) return

    updateLocationPin(pinId, { info: pinInfo })
  }, [pinId, disabled, pinInfo])

  // Connected Place ID changed
  useEffect(() => {
    if (disabled) return
    const pin = getLocationPin(pinId, true)

    if (!pin) return

    // NOTE: Below you can find a type check that we were performing before https://github.com/WorkWave/route-manager/pull/826
    // The team didn't remember the motivation behind the check but preferred to track the ref with that comment.

    // if (typeof connectedPlaceId !== 'string') return

    updateLocationPin(pinId, prevPin =>
      produce(prevPin, draft => {
        draft.pin.type = connectedPlaceId ? 'depot' : pinType
      }),
    )
    // ATTENTION: pinType should be statically defined by the owner form
  }, [pinId, pinType, disabled, connectedPlaceId])
}
